import * as Res from "./res";
import { registerComponent } from "./component";
import { onEvent } from './_dom';
import { ItemBar, itembar } from "./itembar";

class Tabs extends ItemBar {
	constructor(parent) {
		super(parent);
	}

	get activeTab() {
		// this.itemCells are wrappers
		return this.parent.querySelector(".v3-tabs__item--active");
	}

	set activeTab(value) {
		const oldActiveTab = this.activeTab;
		if (oldActiveTab) {
			const oldActiveTabIsMenuItem = oldActiveTab.role == "menuitem";
			oldActiveTab.classList.remove("v3-tabs__item--active");
			oldActiveTab.removeAttribute(oldActiveTabIsMenuItem ? "aria-current" : "aria-selected");
		}
		const newActiveTab = this.itemCells.find(item => item.firstElementChild === value)?.firstElementChild;
		if (newActiveTab) {
			const newActiveTabIsMenuItem = newActiveTab.role == "menuitem";
			newActiveTab.classList.add("v3-tabs__item--active");
			newActiveTab.setAttribute(newActiveTabIsMenuItem ? "aria-current" : "aria-selected", "true");
		}
	}

	#toggleItemClasses = (itemCell, isMenu) => {
		const item = itemCell.firstElementChild;
		item.classList.toggle("v3-tabs__item", !isMenu);
		item.classList.toggle("v3-menu__item", isMenu);
		item.setAttribute("role", isMenu ? "menuitem" : this.#tabrole);
	};

	#sendTabClickEvent = (event, item) => {
		var tabClickEvent = new CustomEvent("tab.click", { detail: item, bubbles: true, cancelable: true });
		this.parent.dispatchEvent(tabClickEvent);
		if (tabClickEvent.defaultPrevented) {
			event.preventDefault();
		}
	};

	#handleHorizontalArrows = (event, target) => {
		let dir = 0;
		switch (event.key) {
			case "ArrowLeft":
				dir = -2;
			// fallthrough
			case "ArrowRight":
				{
					dir++;
					const tabs = Array.from(this.parent.querySelectorAll(`[role="${this.#tabrole}"]`));
					if (tabs.length) {
						const index = tabs.indexOf(target);
						let newIndex = index + dir;
						if (newIndex < 0) newIndex = tabs.length - 1;
						if (newIndex >= tabs.length) newIndex = 0;
						if (index >= 0 && newIndex != index) {
							tabs[index].tabIndex = -1;
							tabs[newIndex].tabIndex = 0;
							tabs[newIndex].focus();
						}
					}
					break;
				}
			case " ":
			case "Enter":
		}
	};

	refresh(options = {}) {
		options = { classPrefix: "v3-tabs", moreTitle: Res.getString('dbui.tabs.more'), alwaysKeepItem: () => this.activeTab, toggleItemMode: this.#toggleItemClasses, ...options };
		this.#tabrole = options.tabrole ?? "tab";
		super.refresh(options);
		onEvent(this.parent, ".v3-tabs__item:not(.v3-tabs__overflow),.v3-tabs__menu .v3-menu__item", "click", this.#sendTabClickEvent);
		onEvent(this.parent, `[role="${this.#tabrole}"]`, "keydown", this.#handleHorizontalArrows);
	}

	/** @type {string} */
	#tabrole;
}

// needs referential integrity
function createTab(element, options) {
	const tab = new Tabs(element);
	tab.refresh(options);
	return tab;
}

const tabs = registerComponent(createTab);

function initHeaderTabs(context) {
	const tabElement = (context || document).querySelector(".v3-toptabs");
	if (tabElement) {
		tabElement.role = "menubar";
		tabs(tabElement, { classPrefix: ["v3-toptabs", "v3-tabs"], tabrole: "menuitem" });
	}
}

function initActionsTabs(context) {
	const actionBar = (context || document).querySelector(".v3-header__menu2>.v3-layout__actions");
	if (actionBar) {
		// setup foldable item bar
		actionBar.classList.add("v3-layout-actionbar");
		itembar(actionBar, {
			classPrefix: "v3-layout-actionbar",
			badgeClass: "v3-button",
			moreTitle: "",
			toggleItemMode: (itemCell, isMenu) => {
				const item = itemCell.firstElementChild;
				item.classList.toggle("v3-button", !isMenu); 
				item.classList.toggle("v3-menu__item", isMenu);
				item.role = isMenu ? "menuitem" : item.tagName != "BUTTON" ? "button" : "";
			}
		});
		// track form element visibility and hide action bar when form is out of the view.
		const formElement = actionBar.dataset.form && document.getElementById(actionBar.dataset.form);
		if (formElement) {
			const topbar = document.getElementById("td-toptab");
			const topNavHeight = topbar.getBoundingClientRect().height;
			new IntersectionObserver(([entry]) => {
				actionBar.style.visibility = entry.isIntersecting ? "visible" : "hidden";
			}, { root: document.body, rootMargin: `-${topNavHeight}px 0px 0px 0px` }).observe(formElement);
		}
	}
}

export { tabs, initHeaderTabs, initActionsTabs };